"use client";

import { useId, type ComponentProps } from "react";
import { Input } from "./input";
import { cn } from "@/lib/utils";

/** Currency entry for signed amounts; keeps decimals and partial input intact. */
export function SignedCurrencyInput({
  currency,
  value,
  onValueChange,
  className,
  ...props
}: Omit<ComponentProps<"input">, "value" | "onChange" | "type"> & {
  currency: string;
  value: string;
  onValueChange: (value: string) => void;
}) {
  const currencyId = useId();
  const [whole, fraction] = value.split(".");
  const formatted = /^[-+]?\d+(?:\.\d{0,2})?$/.test(value)
    ? `${Number(whole).toLocaleString("en-US")}${fraction !== undefined ? `.${fraction}` : ""}`
    : value;
  return (
    <div className="relative">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
      >
        $
      </span>
      <Input
        {...props}
        aria-describedby={[props["aria-describedby"], currencyId]
          .filter(Boolean)
          .join(" ")}
        type="text"
        inputMode="decimal"
        value={formatted}
        className={cn("h-12 pl-8 pr-14 text-base md:text-base", className)}
        onChange={(event) => {
          const next = event.target.value.replace(/[$,\s]/g, "");
          if (/^[-+]?\d*(?:\.\d{0,2})?$/.test(next)) onValueChange(next);
        }}
      />
      <span
        id={currencyId}
        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
      >
        {currency}
      </span>
    </div>
  );
}
